import { useCallback, useState } from 'react';
import {
  Form,
  FormSizes,
  Button,
  ButtonVariants,
  ButtonSizes,
  Modal,
  ModalSizes,
  TextInput,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
  FormValidateOn,
} from '@carboledger/ui';
import { isFormDirty } from '@/lib/utils';
import { ConfirmDialog } from '@/components/feedback';
import {
  ReferenceIdField,
  FacilityField,
  MaterialField,
  SoldToField,
  ShippingToField,
  DateOfDispatchField,
  VesselNameField,
} from '@/components/common';
import { useTranslations } from '@/hooks';
import { outgoingShipmentValidation } from '../outgoingShipmentsValidation';
import { ShipmentQuantityField } from './ShipmentQuantityField';
import { ShipmentTerminalField } from './ShipmentTerminalField';
import { ShipmentUnitField } from './ShipmentUnitField';
import styles from './OutgoingShipmentForm.module.scss';

// Every field the form owns; a missing key here would make its input uncontrolled.
export const EMPTY_OUTGOING_SHIPMENT_FORM = Object.freeze({
  // The server's own auto-generated shipment numbers use this prefix.
  idPrefix: 'SP',
  idNumber: '',
  facilityId: '',
  terminalId: '',
  materialId: '',
  soldToId: '',
  shippingToId: '',
  shipmentQuantity: '',
  shipmentQuantityUnit: '',
  // Not a control — the chosen material's display_unit row, which the payload needs.
  displayUnitId: '',
  dateOfDispatch: '',
  vesselName: '',
});

// site → terminal → material → unit. Picking a parent drops every stale descendant.
const FACILITY_CLEARS = Object.freeze(['terminalId', 'materialId', 'shipmentQuantityUnit']);
const MATERIAL_CLEARS = Object.freeze(['shipmentQuantityUnit']);

/**
 * Props:
 *   title          form header text
 *   description    optional explanatory paragraph, shown above the fields
 *   submitLabel    primary button text
 *   initialValues  starting values (EMPTY_OUTGOING_SHIPMENT_FORM for Add)
 *   onSubmit       (values, helpers) => void — runs only once validation passes
 *   onClose        ✕ and Cancel
 *   isSubmitting   disables the primary button while saving
 *   options        backend-driven lists (facilities, customers, sites, materials, certificateType)
 *   mode           'add' | 'edit' — edit keeps the saved shipment number
 *   lockedExceptQuantity  something is allocated, so only the quantity may change
 *   lockedFromOrder  fulfilling an order: facility/material/sold to/shipping to are inherited and locked
 *   lockedDisplayValues  lockedFromOrder's own read-only text — the order's facility/material/sold to/shipping to names, never options ids;
 *                        `terminal` only for an order holding a free-text terminal with no terminal record
 *   assignedByUnit unit symbol → quantity the linked orders already claim, the entry's floor
 *   asModal        renders as a floating Modal (2-column fields) instead of a full FormShell page
 *   open           asModal only — whether the Modal is shown
 */
export const OutgoingShipmentForm = ({
  title,
  description,
  submitLabel,
  initialValues,
  onSubmit,
  onClose,
  isSubmitting = false,
  options = {},
  mode = 'add',
  lockedExceptQuantity = false,
  lockedFromOrder = false,
  lockedDisplayValues = {},
  assignedByUnit = null,
  asModal = false,
  open = true,
}) => {
  const t = useTranslations();

  const [confirmingClose, setConfirmingClose] = useState(false);
  const askBeforeClosing = useCallback(() => setConfirmingClose(true), []);
  const keepEditing = useCallback(() => setConfirmingClose(false), []);
  // The modal stays mounted across opens (its data is cached), so this must clear its own flag.
  const confirmDiscard = useCallback(() => {
    setConfirmingClose(false);
    onClose();
  }, [onClose]);

  // The order already fixed these; only a still-blank terminal stays open for the user to pick.
  const orderDetailsLocked = lockedExceptQuantity || lockedFromOrder;
  const terminalLocked =
    lockedExceptQuantity || (lockedFromOrder && Boolean(initialValues?.terminalId));

  return (
    <Form
      validateOn={FormValidateOn.change}
      size={FormSizes.large}
      initialValues={initialValues}
      onSubmit={onSubmit}
      className={styles.form}
    >
      {({ values, submit }) => {
        const requestClose = isFormDirty(values, initialValues) ? askBeforeClosing : onClose;

        // Built once, arranged into either the page's 4-column grid or the modal's 2-column one.
        const shipmentIdField = (
          <ReferenceIdField
            label={t('FIELD_SHIPMENT_ID')}
            prefixLabel={t('SHIPMENT_FORM_SHIPMENT_ID_PREFIX')}
            numberLabel={t('SHIPMENT_FORM_SHIPMENT_ID_NUMBER')}
            // The update route runs no uniqueness check, so a saved id stays put.
            readOnly={mode === 'edit'}
            rules={outgoingShipmentValidation.idNumber}
          />
        );
        // Fulfilling an order: these are fixed values, not a pickable option.
        const facilityField = lockedFromOrder ? (
          <TextInput
            label={t('FIELD_FACILITY_NAME')}
            value={lockedDisplayValues.facility ?? ''}
            disabled
            readOnly
          />
        ) : (
          <FacilityField
            label={t('FIELD_FACILITY_NAME')}
            options={options.facilities}
            clears={FACILITY_CLEARS}
            disabled={orderDetailsLocked}
          />
        );
        // An order with a free-text terminal keeps it: the server rejects any other pick.
        const terminalField =
          lockedFromOrder && lockedDisplayValues.terminal && !initialValues?.terminalId ? (
            <TextInput
              label={t('FIELD_TERMINAL')}
              value={lockedDisplayValues.terminal}
              disabled
              readOnly
            />
          ) : (
            <ShipmentTerminalField
              sites={options.sites}
              disabled={terminalLocked}
              forOrder={lockedFromOrder}
            />
          );
        const materialField = lockedFromOrder ? (
          <TextInput
            label={t('FIELD_MATERIAL_NAME')}
            value={lockedDisplayValues.material ?? ''}
            disabled
            readOnly
          />
        ) : (
          <MaterialField
            materials={options.materials}
            certificateType={options.certificateType}
            clears={MATERIAL_CLEARS}
            disabled={orderDetailsLocked}
          />
        );
        const soldToField = lockedFromOrder ? (
          <TextInput
            label={t('FIELD_SOLD_TO')}
            value={lockedDisplayValues.soldTo ?? ''}
            disabled
            readOnly
          />
        ) : (
          <SoldToField options={options.customers} disabled={orderDetailsLocked} />
        );
        const shippingToField = lockedFromOrder ? (
          <TextInput
            label={t('FORM_SHIPPING_TO_OPTIONAL')}
            value={lockedDisplayValues.shippingTo ?? ''}
            disabled
            readOnly
          />
        ) : (
          <ShippingToField disabled={orderDetailsLocked} />
        );
        const quantityField = <ShipmentQuantityField assignedByUnit={assignedByUnit} />;
        const unitField = (
          <ShipmentUnitField materials={options.materials} disabled={lockedExceptQuantity} />
        );
        const dateField = <DateOfDispatchField disabled={lockedExceptQuantity} />;
        const vesselField = <VesselNameField disabled={lockedExceptQuantity} />;

        const descriptionNode = description ? (
          <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.secondary}>
            {description}
          </Typography>
        ) : null;

        if (asModal) {
          const modalButtons = [
            { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: requestClose },
            {
              label: submitLabel,
              variant: ButtonVariants.primary,
              onClick: submit,
              disabled: isSubmitting,
              loading: isSubmitting,
            },
          ];

          return (
            <>
              <Modal
                open={open}
                onClose={requestClose}
                size={ModalSizes.medium}
                title={title}
                buttons={modalButtons}
              >
                <div className={styles.modalFields}>
                  <Typography
                    variant={TypographyVariants.bodySm}
                    weight={TypographyWeights.semibold}
                    color={TypographyColors.secondary}
                  >
                    {t('SHIPMENT_FORM_MODAL_HEADING')}
                  </Typography>

                  <div className={styles.modalDivider} />

                  {descriptionNode}

                  <Form.Row columns={2}>
                    <div className={styles.span2}>{shipmentIdField}</div>
                  </Form.Row>

                  <Form.Row columns={2}>
                    <div className={styles.span2}>{materialField}</div>
                  </Form.Row>

                  <Form.Row columns={2}>
                    {facilityField}
                    {terminalField}
                  </Form.Row>

                  <Form.Row columns={2}>
                    {soldToField}
                    {shippingToField}
                  </Form.Row>

                  <Form.Row columns={2}>
                    {quantityField}
                    {unitField}
                  </Form.Row>

                  <Form.Row columns={2}>
                    {dateField}
                    {vesselField}
                  </Form.Row>
                </div>
              </Modal>

              <ConfirmDialog
                open={confirmingClose}
                title={t('COMMON_DISCARD_TITLE')}
                bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
                description={t('COMMON_DISCARD_DESCRIPTION')}
                confirmLabel={t('COMMON_YES')}
                onConfirm={confirmDiscard}
                onCancel={keepEditing}
              />
            </>
          );
        }

        return (
          <>
            <Form.Header title={title} onClose={requestClose} closeLabel={t('COMMON_CLOSE')} />

            <Form.Section className={styles.body}>
              {descriptionNode}

              <Form.Row columns={4}>
                <div className={styles.span2}>{shipmentIdField}</div>

                {facilityField}

                {terminalField}
              </Form.Row>

              <Form.Row columns={4}>
                <div className={styles.span2}>{materialField}</div>

                {soldToField}

                {shippingToField}
              </Form.Row>

              <Form.Row columns={4}>
                {quantityField}

                {unitField}

                {dateField}

                {vesselField}
              </Form.Row>
            </Form.Section>

            <Form.Footer>
              <Button variant={ButtonVariants.neutral} size={ButtonSizes.sm} onClick={requestClose}>
                {t('COMMON_CANCEL')}
              </Button>
              <Button
                type="submit"
                variant={ButtonVariants.primary}
                size={ButtonSizes.sm}
                disabled={isSubmitting}
              >
                {submitLabel}
              </Button>
            </Form.Footer>

            <ConfirmDialog
              open={confirmingClose}
              title={t('COMMON_DISCARD_TITLE')}
              bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
              description={t('COMMON_DISCARD_DESCRIPTION')}
              confirmLabel={t('COMMON_YES')}
              onConfirm={confirmDiscard}
              onCancel={keepEditing}
            />
          </>
        );
      }}
    </Form>
  );
};
